/* brand-marquee.js — the home "Brands we carry" strip (sections/brand-marquee.liquid).
 *
 * Luke, 2026-09-13, live theme: "brands we carry should also be swipeable to
 * cycle through brands. If I mouseover the brands as they are moving the
 * brands carousel freezes up and stays stuck."
 *
 * Without this file the strip is the CSS keyframe marquee (theme.css
 * .brandmarq__track): two byte-identical runs, translateX(0 -> -50%), paused
 * while the scroller is hovered or a tile has keyboard focus. That is the
 * no-JS state and it stays correct on its own. With it, the strip becomes a
 * scroller the shopper can move:
 *
 *  - the drift continues when idle, at the SAME px/s the keyframe gave it
 *    (one run width over --marq-n x 3.5s, x 5s under 900px), and takes over
 *    from wherever the keyframe had got to, so nothing jumps at init;
 *  - pointer down or hover (mouse) or keyboard focus (:focus-visible) holds
 *    it; release / leave / blur lets it go. Holds are a Set, exactly like
 *    hero-slideshow.js: a hold that was never released is the whole freeze
 *    bug, so every hold has one owner that both adds and deletes it;
 *  - drag with a mouse, swipe on touch, momentum on release, wrapping across
 *    the seam between the two runs (x lives in (-runWidth, 0]; the runs are
 *    identical so x and x - runWidth paint the same pixels);
 *  - ArrowLeft / ArrowRight nudge the strip while focus is inside it; tiles
 *    stay in the tab order (the clone's are tabindex=-1 already);
 *  - prefers-reduced-motion: no drift at all. theme.css already turns the
 *    scroller into a native overflow-x:auto strip with the clone hidden, so
 *    touch swipe and keyboard scrolling are the browser's; this file adds only
 *    mouse drag (scrollLeft) and the arrow keys, with no momentum. Watched
 *    live, like the hero.
 *
 * THE FREEZE. Chrome focuses an <a> on mousedown. A press on a tile that did
 * not end as a click (the strip moved under the pointer, or the press became
 * a drag) left the tile focused, .brandmarq:focus-within kept the keyframe
 * paused, and the pointer leaving changed nothing — reproduced with Playwright
 * on the branch preview 2026-09-13: hover-then-leave resumed at 49 px/s,
 * mousedown/drag/up-then-leave stayed at 0 px/s. The CSS rule now keys on
 * :focus-visible (theme.css), and here a pointer-focused tile never holds
 * the strip: only focus that the browser would draw a ring for does.
 *
 * SWIPE mechanics, borrowed from hero-slideshow.js (PR #238) and for the same
 * reasons: touch-action:pan-y on the scroller (theme.css) so a vertical swipe
 * that starts on the strip scrolls the page (we see pointercancel, never a
 * drag); no pointer capture, so a tap on a tile still navigates; dragstart is
 * cancelled ONLY mid-drag, because the tiles are <a><img> and a native image
 * drag would kill the pointer stream; a mouse drag that moved must not also
 * click the tile it started on — a one-shot capture-phase click guard, retired
 * on the next task. Decided on release: a press that moves < 6px is a click.
 */
(() => {
  const MQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const NARROW = window.matchMedia ? window.matchMedia('(max-width: 899px)') : null;
  const reduced = () => !!(MQ && MQ.matches);
  const THRESHOLD = 6;        // px before a press is a drag
  const KEY_NUDGE = 1.4;      // px/ms: ArrowLeft/Right give the strip this much momentum
  const DECAY = 0.94;         // momentum retained per 60fps frame
  const MAX_V = 3;            // px/ms cap on fling velocity

  function init(root) {
    if (root.dataset.bmReady) return;
    const scroller = root.querySelector('.brandmarq__scroller');
    const track = root.querySelector('.brandmarq__track');
    const runs = track ? track.querySelectorAll('.brandmarq__run') : [];
    if (!scroller || !track || runs.length < 2) return;
    root.dataset.bmReady = '1';

    const n = parseFloat(getComputedStyle(track).getPropertyValue('--marq-n')) || 40;
    let runW = 0;
    let x = 0;                   // translateX in loop mode, (-runW, 0]
    let v = 0;                   // momentum, px/ms (+ = strip moves right)
    let drag = null;
    let raf = 0;
    let last = 0;
    let visible = true;
    const holds = new Set();     // 'hover' | 'press' | 'focus' | 'hidden' | 'offscreen'

    const measure = () => { runW = runs[1].offsetLeft - runs[0].offsetLeft; };
    const speed = () => runW / (n * (NARROW && NARROW.matches ? 5 : 3.5) * 1000);
    const wrap = () => { if (runW > 0) x = -(((-x) % runW) + runW) % runW; };
    const paint = () => { wrap(); track.style.transform = `translate3d(${x}px,0,0)`; };

    // ---- loop mode: take over from the keyframe at its current position -----
    const takeOver = () => {
      measure();
      const m = getComputedStyle(track).transform;
      if (m && m !== 'none') {
        try { x = new DOMMatrix(m).m41; } catch (e) { x = 0; }
      }
      track.style.animation = 'none';
      paint();
      schedule();
    };
    const handBack = () => {
      cancelAnimationFrame(raf); raf = 0;
      track.style.transform = '';
      track.style.animation = '';
      v = 0;
    };

    const idle = () => !drag && v === 0 && (holds.size > 0 || !visible || reduced());
    const tick = (t) => {
      raf = 0;
      const dt = Math.min(64, t - (last || t));
      last = t;
      if (!drag) {
        if (v !== 0) {
          x += v * dt;
          v *= Math.pow(DECAY, dt / 16.7);
          if (Math.abs(v) < 0.01) v = 0;
        } else if (!idle()) {
          x -= speed() * dt;
        }
        paint();
      }
      if (!idle()) raf = requestAnimationFrame(tick);
      else last = 0;
    };
    const schedule = () => { if (!raf && !reduced()) { last = 0; raf = requestAnimationFrame(tick); } };

    // ---- holds: each has exactly one owner that adds AND deletes it ---------
    const hold = (k) => { holds.add(k); };
    const release = (k) => { holds.delete(k); schedule(); };

    scroller.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold('hover'); });
    scroller.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') release('hover'); });
    root.addEventListener('focusin', (e) => {
      // a pointer-focused tile (Chrome focuses <a> on mousedown) must not hold
      // the strip: that was the freeze. Keyboard focus — the ring — does.
      if (e.target && e.target.matches && e.target.matches(':focus-visible')) hold('focus');
      else release('focus');
    });
    root.addEventListener('focusout', (e) => {
      if (!e.relatedTarget || !root.contains(e.relatedTarget)) release('focus');
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden) hold('hidden'); else release('hidden'); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries.some((en) => en.isIntersecting);
        if (visible) schedule();
      }, { threshold: 0 }).observe(root);
    }

    // ---- drag / swipe -------------------------------------------------------
    const onMove = (e) => {
      const d = drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x0;
      if (!d.moved) {
        if (Math.abs(dx) < THRESHOLD) return;
        d.moved = true;
        root.classList.add('is-dragging');
      }
      if (reduced()) {
        scroller.scrollLeft = d.s0 - dx;
      } else {
        x = d.sx + dx;
        paint();
      }
      d.samples.push([e.timeStamp, e.clientX]);
      if (d.samples.length > 5) d.samples.shift();
    };
    const endDrag = (fling) => {
      const d = drag;
      if (!d) return;
      drag = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      root.classList.remove('is-dragging');
      if (d.moved) {
        if (fling && !reduced() && d.samples.length > 1) {
          const [t0, x0] = d.samples[0];
          const [t1, x1] = d.samples[d.samples.length - 1];
          v = t1 > t0 ? Math.max(-MAX_V, Math.min(MAX_V, (x1 - x0) / (t1 - t0))) : 0;
          if (Math.abs(v) < 0.05) v = 0;
        }
        // the click that follows this pointerup would navigate to the tile the
        // drag started on
        const guard = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        scroller.addEventListener('click', guard, { capture: true, once: true });
        setTimeout(() => scroller.removeEventListener('click', guard, { capture: true }), 0);
        // Chrome focused the tile on mousedown; a pointer-focused tile is not a
        // hold (see focusin), but it would also draw no ring and confuse the
        // next Tab, so let it go
        const a = document.activeElement;
        if (a && root.contains(a) && a.matches && !a.matches(':focus-visible')) a.blur();
      }
      release('press');
    };
    const onUp = (e) => { if (drag && e.pointerId === drag.id) endDrag(true); };
    const onCancel = (e) => { if (drag && e.pointerId === drag.id) endDrag(false); };
    scroller.addEventListener('pointerdown', (e) => {
      if (drag || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (!runW) measure();
      drag = { id: e.pointerId, x0: e.clientX, sx: x, s0: scroller.scrollLeft, moved: false, samples: [[e.timeStamp, e.clientX]] };
      v = 0;
      hold('press');
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
    });
    root.addEventListener('dragstart', (e) => { if (drag && drag.moved) e.preventDefault(); });

    // ---- keyboard -----------------------------------------------------------
    root.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const dir = e.key === 'ArrowLeft' ? 1 : -1;
      if (reduced()) {
        scroller.scrollBy({ left: -dir * 240, behavior: 'auto' });
      } else {
        v = Math.max(-MAX_V, Math.min(MAX_V, v + dir * KEY_NUDGE));
        schedule();
      }
    });

    // ---- mode ---------------------------------------------------------------
    const mode = () => { if (reduced()) handBack(); else takeOver(); };
    if (MQ && MQ.addEventListener) MQ.addEventListener('change', mode);
    let rt = 0;
    window.addEventListener('resize', () => {
      clearTimeout(rt);
      rt = setTimeout(() => { if (!reduced()) { measure(); paint(); } }, 120);
    });
    mode();
  }

  const boot = () => document.querySelectorAll('[data-brand-marquee]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-brand-marquee]').forEach(init));
})();
